import React, { useState } from 'react'
import { Link } from '@inertiajs/react'
import Layout from './Layout'
import { formatPrice, formatDateShort, getStatusClasses, capitalize } from '../../utils/formatters'
import { BarChart, LineChart, DoughnutChart } from '../../Components/Charts'
import { StarIcon } from '../../Components/StarRating'

export default function Dashboard({
  account,
  accounts,
  navigation,
  stats,
  recent_orders,
  is_account_admin,
  analytics,
  bookings_enabled
}) {
  const orderUrl = `${window.location.origin}/${account.slug}/order`
  const bookingUrl = `${window.location.origin}/${account.slug}/booking`
  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title="Dashboard"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatsCard
          title="Orders Today"
          value={stats.total_orders_today}
        />
        <StatsCard
          title="Pending Orders"
          value={stats.pending_orders}
          highlight={stats.pending_orders > 0}
        />
        <StatsCard
          title="Revenue Today"
          value={formatPrice(stats.total_revenue_today)}
        />
        <StatsCard
          title="Menu Items"
          value={stats.menu_items_count}
        />
      </div>

      {/* Quick Links */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Links</h3>

        <ShareableLink label="Customer Order Link" url={orderUrl} shareTitle={`Order from ${account.name}`} shareText={`Order food and drinks from ${account.name}`} />
        {bookings_enabled && (
          <ShareableLink label="Customer Booking Link" url={bookingUrl} shareTitle={`Book at ${account.name}`} shareText={`Book a venue at ${account.name}`} />
        )}

        {/* Links Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          <QuickLink href="/kitchen" icon="kitchen" primary>
            Kitchen Display
          </QuickLink>
          <QuickLink href="/manage/orders" icon="orders">
            Orders
          </QuickLink>
          <QuickLink href="/manage/menu_items" icon="menu">
            Menu
          </QuickLink>
          <QuickLink href="/manage/positions" icon="qr">
            QR Codes
          </QuickLink>
          {is_account_admin && (
            <QuickLink href="/manage/team" icon="team">
              Team
            </QuickLink>
          )}
          <QuickLink href="/manage/settings" icon="settings">
            Settings
          </QuickLink>
        </div>
      </div>

      {/* Analytics Section */}
      {analytics && (
        <AnalyticsSection analytics={analytics} />
      )}

      {/* Recent Orders */}
      <div className="bg-white shadow rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Orders</h3>
          <Link
            href="/manage/orders"
            className="text-sm text-sky-600 hover:text-sky-500"
          >
            View all
          </Link>
        </div>

        {recent_orders.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-500">
            No recent orders
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {recent_orders.map((order) => (
              <li key={order.id} className="px-4 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      #{order.order_number}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDateShort(order.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-900">
                      {formatPrice(order.total_pence)}
                    </span>
                    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusClasses(order.status)}`}>
                      {capitalize(order.status)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  )
}

function StatsCard({ title, value, highlight = false }) {
  return (
    <div className={`bg-white overflow-hidden shadow rounded-lg ${highlight ? 'ring-2 ring-yellow-400' : ''}`}>
      <div className="px-4 py-5 sm:p-6">
        <dt className="text-sm font-medium text-gray-500 truncate">
          {title}
        </dt>
        <dd className="mt-1 text-3xl font-semibold text-gray-900">
          {value}
        </dd>
      </div>
    </div>
  )
}

const QUICK_LINK_ICONS = {
  kitchen: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  ),
  orders: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  ),
  menu: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
  ),
  qr: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
  ),
  team: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  ),
  settings: (
    <>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </>
  )
}

function QuickLink({ href, icon, primary = false, children }) {
  const baseClasses = "flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition"
  const variantClasses = primary
    ? "bg-sky-600 text-white hover:bg-sky-700"
    : "bg-gray-100 text-gray-700 hover:bg-gray-200"

  return (
    <Link href={href} className={`${baseClasses} ${variantClasses}`}>
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        {QUICK_LINK_ICONS[icon]}
      </svg>
      {children}
    </Link>
  )
}

function AnalyticsSection({ analytics }) {
  const hasOrders = analytics.period.total_orders > 0

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">Analytics</h2>
        <span className="text-sm text-gray-500">Last 28 days</span>
      </div>

      {!hasOrders ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No order data yet</h3>
          <p className="mt-1 text-sm text-gray-500">Analytics will appear here once you receive orders.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <ChartCard title="Daily Revenue">
              <DailyRevenueChart data={analytics.daily_revenue} />
            </ChartCard>

            <ChartCard title="Orders by Time of Day">
              <HourlyDistributionChart data={analytics.hourly_distribution} />
            </ChartCard>

            <ChartCard title="Revenue by Category">
              <CategoryRevenueChart data={analytics.category_revenue} />
            </ChartCard>

            <ChartCard title="Indoor vs Outdoor">
              <LocationTypeChart data={analytics.location_type_split} />
            </ChartCard>
          </div>

          {analytics.ratings && analytics.ratings.count > 0 && (
            <div className="mb-6">
              <RatingsCard ratings={analytics.ratings} />
            </div>
          )}

          {analytics.position_breakdown.length > 0 && (
            <ChartCard title="Top Order Locations">
              <PositionBreakdownChart data={analytics.position_breakdown} />
            </ChartCard>
          )}
        </>
      )}
    </div>
  )
}

function RatingsCard({ ratings }) {
  const maxCount = Math.max(...[5, 4, 3, 2, 1].map((star) => ratings.distribution[star] || 0), 1)

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-lg font-bold text-gray-900">Ratings</h3>
      <p className="text-sm text-gray-500 mb-6">Customer ratings across the reporting period</p>

      <div className="flex flex-col sm:flex-row sm:items-center gap-6">
        <div className="shrink-0 sm:w-44">
          <div className="flex items-center gap-2">
            <span className="text-4xl font-bold text-gray-900">{ratings.average}</span>
            <StarIcon className="w-7 h-7 text-yellow-400" />
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {ratings.count} rated order{ratings.count === 1 ? '' : 's'}
          </p>
        </div>

        <div className="flex-1 space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = ratings.distribution[star] || 0
            return (
              <div key={star} className="flex items-center gap-3">
                <span className="flex items-center justify-end gap-1 w-9 text-sm text-gray-700">
                  {star}
                  <StarIcon className="w-4 h-4 text-yellow-400" />
                </span>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-yellow-400 rounded-full"
                    style={{ width: `${(count / maxCount) * 100}%` }}
                  />
                </div>
                <span className="w-8 text-right text-sm text-gray-700">{count}</span>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white shadow rounded-lg p-6">
      <h3 className="text-sm font-medium text-gray-700 mb-4">{title}</h3>
      <div className="h-64">
        {children}
      </div>
    </div>
  )
}

function DailyRevenueChart({ data }) {
  const chartData = {
    labels: data.map(d => {
      const date = new Date(d.date)
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
    }),
    datasets: [{
      data: data.map(d => d.revenue_pence / 100),
      borderColor: 'rgb(14, 165, 233)',
      backgroundColor: 'rgba(14, 165, 233, 0.1)',
      fill: true
    }]
  }

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `£${context.raw.toFixed(2)}`
        }
      }
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => `£${value}`
        }
      }
    }
  }

  return <LineChart data={chartData} options={options} />
}

function HourlyDistributionChart({ data }) {
  const chartData = {
    labels: data.map(d => d.label),
    datasets: [{
      data: data.map(d => d.order_count),
      backgroundColor: 'rgb(14, 165, 233)',
      borderRadius: 4
    }]
  }

  return <BarChart data={chartData} />
}

function CategoryRevenueChart({ data }) {
  if (!data || data.length === 0) {
    return <EmptyChartState message="No category data available" />
  }

  const colors = {
    food: 'rgb(34, 197, 94)',
    drinks: 'rgb(168, 85, 247)',
    unknown: 'rgb(156, 163, 175)'
  }

  const chartData = {
    labels: data.map(d => capitalize(d.category)),
    datasets: [{
      data: data.map(d => d.revenue_pence / 100),
      backgroundColor: data.map(d => colors[d.category] || colors.unknown)
    }]
  }

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          label: (context) => `£${context.raw.toFixed(2)}`
        }
      }
    }
  }

  return (
    <div className="flex items-center h-full">
      <div className="w-1/2 h-full">
        <DoughnutChart data={chartData} options={options} />
      </div>
      <div className="w-1/2 pl-4">
        {data.map((item, index) => (
          <div key={item.category} className="flex items-center justify-between py-2">
            <div className="flex items-center">
              <span
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: colors[item.category] || colors.unknown }}
              />
              <span className="text-sm text-gray-600">{capitalize(item.category)}</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {formatPrice(item.revenue_pence)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function LocationTypeChart({ data }) {
  if (!data || data.length === 0) {
    return <EmptyChartState message="No location data available" />
  }

  const colors = {
    'Indoor': 'rgb(59, 130, 246)',
    'Outdoor': 'rgb(34, 197, 94)',
    'No Location': 'rgb(156, 163, 175)'
  }

  const chartData = {
    labels: data.map(d => d.label),
    datasets: [{
      data: data.map(d => d.order_count),
      backgroundColor: data.map(d => colors[d.label] || colors['No Location'])
    }]
  }

  return (
    <div className="flex items-center h-full">
      <div className="w-1/2 h-full">
        <DoughnutChart data={chartData} />
      </div>
      <div className="w-1/2 pl-4">
        {data.map((item) => (
          <div key={item.label} className="flex items-center justify-between py-2">
            <div className="flex items-center">
              <span
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: colors[item.label] || colors['No Location'] }}
              />
              <span className="text-sm text-gray-600">{item.label}</span>
            </div>
            <span className="text-sm font-medium text-gray-900">
              {item.order_count} orders
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function PositionBreakdownChart({ data }) {
  const chartData = {
    labels: data.map(d => d.name),
    datasets: [{
      data: data.map(d => d.order_count),
      backgroundColor: data.map(d =>
        d.type === 'indoor' ? 'rgb(59, 130, 246)' : 'rgb(34, 197, 94)'
      ),
      borderRadius: 4
    }]
  }

  const options = {
    plugins: {
      tooltip: {
        callbacks: {
          afterLabel: (context) => {
            const item = data[context.dataIndex]
            return `Revenue: ${formatPrice(item.revenue_pence)}`
          }
        }
      }
    }
  }

  return <BarChart data={chartData} options={options} horizontal />
}

function ShareableLink({ label, url, shareTitle, shareText }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url })
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Failed to share:', err)
      }
    } else {
      handleCopy()
    }
  }

  return (
    <div className="mb-4 p-4 bg-gray-50 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <div className="flex gap-2">
          <button
            onClick={handleCopy}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition"
          >
            {copied ? (
              <>
                <svg className="w-4 h-4 mr-1 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Copied!
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy
              </>
            )}
          </button>
          <button
            onClick={handleShare}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 transition"
          >
            <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Share
          </button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-sm text-gray-600 truncate font-mono">{url}</span>
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:text-sky-700">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    </div>
  )
}

function EmptyChartState({ message }) {
  return (
    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
      {message}
    </div>
  )
}
